import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Card,
  Container,
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Lock, CheckCircle } from '@mui/icons-material';
import { DESIGN_TOKENS } from '../design-system/tokens/colors';
import { useAuth } from '../hooks/useAuth';
import { asaasService } from '../services/asaasService';
import { apiClientBff } from '../services/apiBff';
import { API_ENDPOINTS } from '../config/api';

export const FinishRegistrationPage: React.FC = () => {
  const { t } = useTranslation('forms');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const emailParam = searchParams.get('email') || '';
  const tokenParam = searchParams.get('token') || '';
  
  const [formData, setFormData] = useState({
    username: '', // This will be the full name
    password: '',
    confirmPassword: '',
    token: tokenParam,
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(true);

  // Verify access on mount
  useEffect(() => {
    const verifyAccess = async () => {
      if (!emailParam) {
        navigate('/auth');
        return;
      }

      try {
        // Check if has subscription
        const asaasCheck: any = await asaasService.checkAccessByEmail(emailParam, tokenParam ? { token: tokenParam } : undefined);
        const hasAccess = Boolean(asaasCheck?.accessGranted);

        if (!hasAccess) {
           setError(t('finish_registration.error_subscription_not_found'));
           setTimeout(() => navigate('/auth'), 3000);
           return;
        }

        // Auto-generate username from Asaas name if available
        const customerName = asaasCheck.customer?.name || asaasCheck.subscription?.customer?.name || '';
        if (customerName) {
            // Normalize: remove accents, lowercase, replace spaces with underscores, remove special chars
            const username = customerName
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, '');
            
            // Add random suffix to ensure uniqueness
            const randomSuffix = Math.floor(Math.random() * 1000);
            setFormData(prev => ({ ...prev, username: `${username}_${randomSuffix}` }));
        } else {
            // Fallback: Generate from email if no name found
            const localPart = emailParam.split('@')[0].replace(/[^a-z0-9]/gi, '').toLowerCase();
            const randomSuffix = Math.floor(Math.random() * 1000);
            setFormData(prev => ({ ...prev, username: `${localPart}_${randomSuffix}` }));
        }

        setVerifying(false);
      } catch (err) {
        console.error('Verification error:', err);
        setError(t('finish_registration.error_verification'));
      }
    };

    verifyAccess();
  }, [emailParam, navigate, t, tokenParam]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError(t('finish_registration.error_passwords_mismatch'));
      return;
    }

    if (formData.password.length < 6) {
        setError(t('finish_registration.error_password_length'));
        return;
    }

    if (!formData.token) {
        setError(t('finish_registration.error_token_required'));
        return;
    }

    setIsLoading(true);
    try {
      // 1. Verify Token and Reset Password
      // Since the user was auto-created in AuthPage, we use the reset-password endpoint
      // to set their chosen password using the token they received.
      
      try {
        await apiClientBff.authPost(API_ENDPOINTS.AUTH.RESET_PASSWORD, { 
          token: formData.token.trim(), 
          newPassword: formData.password 
        });
      } catch (error: any) {
         const status = error.status || 500;
         if (status === 400 || status === 401 || status === 404 || status === 410) {
            throw new Error(t('finish_registration.error_token_invalid'));
         }
         throw new Error(t('finish_registration.error_reset_password'));
      }

      // 2. Login with the new password
      const loginResult = await login({
        email: emailParam,
        password: formData.password
      });

      if (loginResult.success) {
        navigate('/dashboard');
      } else {
        navigate('/auth?message=registration_success_login_failed');
      }

    } catch (err: any) {
      console.error('Registration/Reset error:', err);
      setError(err.message || t('finish_registration.error_generic'));
    } finally {
      setIsLoading(false);
    }
  };

  if (verifying) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: DESIGN_TOKENS.colors.background.default }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: DESIGN_TOKENS.colors.background.default 
    }}>
      {/* Header Bar */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
            <img src="/logo.svg" alt="MUU" style={{ height: 32 }} />
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 8 }}>
        <Card sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <CheckCircle sx={{ fontSize: 48, color: DESIGN_TOKENS.colors.status.success[500], mb: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: DESIGN_TOKENS.colors.text.primary }}>
                    {t('finish_registration.title')}
                </Typography>
                <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.secondary, whiteSpace: 'pre-line' }}>
                    {t('finish_registration.subtitle')}
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                    label={t('finish_registration.email_label')}
                    value={emailParam}
                    disabled
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Person color="disabled" /></InputAdornment>
                    }}
                    sx={{ bgcolor: DESIGN_TOKENS.colors.neutral[50] }}
                />

                <TextField
                    label={t('finish_registration.username_label')}
                    value={formData.username}
                    onChange={handleChange('username')}
                    required
                    fullWidth
                    placeholder={t('finish_registration.username_placeholder')}
                    helperText={t('finish_registration.username_helper')}
                    sx={{ display: 'none' }} // Hide username field as requested, it is auto-generated
                />

                <TextField
                    label={t('finish_registration.token_label')}
                    value={formData.token}
                    onChange={handleChange('token')}
                    required
                    fullWidth
                    autoFocus
                    placeholder={t('finish_registration.token_placeholder')}
                    helperText={t('finish_registration.token_helper', { email: emailParam })}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>
                    }}
                />

                <TextField
                    label={t('finish_registration.password_label')}
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange('password')}
                    required
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />

                <TextField
                    label={t('finish_registration.confirm_password_label')}
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange('confirmPassword')}
                    required
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>
                    }}
                />

                <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    sx={{
                        mt: 2,
                        py: 1.5,
                        bgcolor: DESIGN_TOKENS.colors.brand.primary,
                        fontWeight: 600,
                        '&:hover': { bgcolor: DESIGN_TOKENS.colors.brand.primaryVariant }
                    }}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('finish_registration.submit')}
                </Button>
            </Box>
        </Card>
      </Container>
    </Box>
  );
};
